import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export type DriverAvailabilityStatus =
  | "Available"
  | "Unavailable"
  | "PartiallyAvailable"
  | "OnLeave"
  | "Sick"
  | "Assigned"
  | "Other";

export interface DriverAvailabilityDto {
  id: string;
  driverId: string;
  driverName: string;
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  reason?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface DriverAvailabilityListQuery {
  driverId?: string;
  startDateTime?: string;
  endDateTime?: string;
  status?: DriverAvailabilityStatus;
  activeOnly?: boolean;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface DriverAvailabilityListResultDto {
  items: DriverAvailabilityDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateDriverAvailabilityRequest {
  driverId: string;
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  reason?: string;
  notes?: string;
  overrideAssignmentConflicts?: boolean;
  overrideReason?: string;
}

export interface UpdateDriverAvailabilityRequest {
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  reason?: string;
  notes?: string;
  isActive: boolean;
  overrideAssignmentConflicts?: boolean;
  overrideReason?: string;
}

export interface DriverAvailabilityConflictDto {
  conflictType: string;
  message: string;
  status?: DriverAvailabilityStatus;
  availabilityId?: string;
  deliveryPlanId?: string;
  deliveryPlanName?: string;
  startDateTime?: string;
  endDateTime?: string;
}

export interface DriverEffectiveScheduleSegmentDto {
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  sourceType: string;
  availabilityId?: string;
  deliveryPlanId?: string;
  deliveryPlanName?: string;
}

export interface DriverAvailabilityImpactDeliveryPlanDto {
  deliveryPlanId: string;
  name: string;
  status: string;
  plannedDepartureTime: string;
  expectedDeliveryTime: string;
}

export interface DriverAvailabilityImpactDispatchDto {
  dispatchRecordId: string;
  dispatchNumber: string;
  status: string;
  expectedDeliveryDate?: string;
}

export interface DriverAvailabilityImpactResultDto {
  driverId: string;
  hasImpacts: boolean;
  requiresManagerOverride: boolean;
  impactedDeliveryPlans: DriverAvailabilityImpactDeliveryPlanDto[];
  impactedDispatches: DriverAvailabilityImpactDispatchDto[];
  messages: string[];
}

export interface PreviewDriverAvailabilityImpactRequest {
  driverId: string;
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  excludeAvailabilityId?: string;
}

export interface DriverAvailabilityCheckResultDto {
  driverId: string;
  isAvailable: boolean;
  effectiveStatus?: string;
  matchingWindow?: DriverEffectiveScheduleSegmentDto;
  conflicts: DriverAvailabilityConflictDto[];
  availabilityWindows: DriverAvailabilityDto[];
  effectiveSchedule?: DriverEffectiveScheduleSegmentDto[];
}

export interface DriverCalendarOverviewQuery {
  startDateTime: string;
  endDateTime: string;
  view?: "day" | "week" | "month";
  driverId?: string;
  search?: string;
  routeId?: string;
  vehicleId?: string;
  greenhouseId?: string;
  statuses?: DriverAvailabilityStatus[];
  activeDriversOnly?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface DriverCalendarFilterOptionDto {
  id: string;
  name: string;
}

export interface DriverCalendarSummaryDto {
  totalDrivers: number;
  availableNow: number;
  assignedNow: number;
  unavailableNow: number;
  notScheduledNow: number;
  conflictsRequiringAction: number;
}

export interface DriverCalendarResourceDto {
  driverId: string;
  driverName: string;
  phone: string;
  defaultVehicleReg?: string;
  isActive: boolean;
  currentStatus: DriverAvailabilityStatus;
  currentSourceType: string;
  hasConflictNow: boolean;
}

export interface DriverCalendarScheduleBlockDto {
  driverId: string;
  driverName: string;
  startDateTime: string;
  endDateTime: string;
  status: DriverAvailabilityStatus;
  sourceType: string;
  availabilityId?: string;
  availabilityReason?: string;
  availabilityNotes?: string;
  deliveryPlanId?: string;
  deliveryPlanName?: string;
  deliveryPlanStatus?: string;
  routeId?: string;
  routeName?: string;
  vehicleId?: string;
  vehicleRegistrationNumber?: string;
  greenhouseIds: string[];
  greenhouseNames: string[];
  dispatches: DriverAvailabilityImpactDispatchDto[];
  isConflict: boolean;
  conflictReason?: string;
  isEditable: boolean;
}

export interface DriverCalendarOverviewResultDto {
  startDateTime: string;
  endDateTime: string;
  view: string;
  summary: DriverCalendarSummaryDto;
  drivers: DriverCalendarResourceDto[];
  blocks: DriverCalendarScheduleBlockDto[];
  routeOptions: DriverCalendarFilterOptionDto[];
  vehicleOptions: DriverCalendarFilterOptionDto[];
  greenhouseOptions: DriverCalendarFilterOptionDto[];
  totalDrivers: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  dataGaps: string[];
}

export interface CheckDriverAvailabilityRequest {
  driverId: string;
  startDateTime: string;
  endDateTime: string;
  excludeDeliveryPlanId?: string;
}

export const driverAvailabilityApi = {
  getPaged: async (query: DriverAvailabilityListQuery): Promise<DriverAvailabilityListResultDto> => {
    const res = await apiClient.get<ApiResponse<DriverAvailabilityListResultDto>>(
      "/drivers/availability/paged",
      { params: query }
    );

    return res.data.data;
  },

  getByDriver: async (
    driverId: string,
    startDateTime: string,
    endDateTime: string,
    activeOnly = true
  ): Promise<DriverAvailabilityDto[]> => {
    const res = await apiClient.get<ApiResponse<DriverAvailabilityDto[]>>(
      `/drivers/availability/by-driver/${driverId}`,
      {
        params: {
          startDateTime,
          endDateTime,
          activeOnly,
        },
      }
    );

    return res.data.data ?? [];
  },

  create: async (data: CreateDriverAvailabilityRequest): Promise<DriverAvailabilityDto> => {
    const res = await apiClient.post<ApiResponse<DriverAvailabilityDto>>(
      "/drivers/availability",
      data
    );

    return res.data.data;
  },

  update: async (id: string, data: UpdateDriverAvailabilityRequest): Promise<DriverAvailabilityDto> => {
    const res = await apiClient.put<ApiResponse<DriverAvailabilityDto>>(
      `/drivers/availability/${id}`,
      data
    );

    return res.data.data;
  },

  deactivate: async (id: string, reason?: string): Promise<void> => {
    await apiClient.post(`/drivers/availability/${id}/deactivate`, { reason });
  },

  previewImpacts: async (
    payload: PreviewDriverAvailabilityImpactRequest
  ): Promise<DriverAvailabilityImpactResultDto> => {
    const res = await apiClient.post<ApiResponse<DriverAvailabilityImpactResultDto>>(
      "/drivers/availability/impacts",
      payload
    );

    return res.data.data;
  },

  check: async (
    payload: CheckDriverAvailabilityRequest
  ): Promise<DriverAvailabilityCheckResultDto> => {
    const res = await apiClient.post<ApiResponse<DriverAvailabilityCheckResultDto>>(
      "/drivers/availability/check",
      payload
    );

    return res.data.data;
  },

  getCalendarOverview: async (
    query: DriverCalendarOverviewQuery
  ): Promise<DriverCalendarOverviewResultDto> => {
    const params = new URLSearchParams();
    params.set("startDateTime", query.startDateTime);
    params.set("endDateTime", query.endDateTime);

    if (query.view) params.set("view", query.view);
    if (query.driverId) params.set("driverId", query.driverId);
    if (query.search) params.set("search", query.search);
    if (query.routeId) params.set("routeId", query.routeId);
    if (query.vehicleId) params.set("vehicleId", query.vehicleId);
    if (query.greenhouseId) params.set("greenhouseId", query.greenhouseId);
    if (typeof query.activeDriversOnly === "boolean") {
      params.set("activeDriversOnly", String(query.activeDriversOnly));
    }
    if (query.pageNumber) params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));

    query.statuses?.forEach((status) => params.append("statuses", status));

    const res = await apiClient.get<ApiResponse<DriverCalendarOverviewResultDto>>(
      "/drivers/availability/calendar",
      { params }
    );

    return res.data.data;
  },
};
